import type { DeviceProfile } from "~/types/midi";

/**
 * Default device profile for external MIDI hardware
 *
 * This profile enables scrambling for common MIDI CCs that work well with most synthesizers.
 * It uses safe min/max ranges to prevent issues.
 *
 * Based on General MIDI CC standards and common synthesizer mappings.
 */
export const DEFAULT_HARDWARE_PROFILE: DeviceProfile = {
  id: "default-hardware",
  name: "Default Hardware",
  deviceName: "Generic MIDI Device",
  userId: null, // System profile
  isPublic: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  ccConfig: [
    // === STANDARD MIDI CCs ===
    {
      number: 1,
      enabled: false, // Disabled by default - can be disruptive
      min: 0,
      max: 127,
      label: "Modulation",
    },
    {
      number: 7,
      enabled: false, // Disabled - dangerous if too low
      min: 80,
      max: 110,
      label: "Volume",
    },
    {
      number: 11,
      enabled: false, // Disabled - same as volume
      min: 80,
      max: 110,
      label: "Expression",
    },
    {
      number: 74,
      enabled: true, // ✅ Filter Cutoff - widely supported
      min: 0,
      max: 127,
      label: "Filter Cutoff",
    },
    {
      number: 71,
      enabled: true, // ✅ Filter Resonance - widely supported
      min: 0,
      max: 127,
      label: "Filter Resonance",
    },

    // === AMP ENVELOPE ===
    {
      number: 73,
      enabled: false, // Disabled - too short causes clicks on hardware, keep preset value
      min: 0,
      max: 127,
      label: "Amp Attack",
    },
    {
      number: 75,
      enabled: true, // ✅ Decay - safe to scramble
      min: 0,
      max: 127,
      label: "Amp Decay",
    },
    {
      number: 70,
      enabled: true, // ✅ Sustain - safe to scramble
      min: 0,
      max: 127,
      label: "Amp Sustain",
    },
    {
      number: 72,
      enabled: false, // Disabled - too short causes abrupt cutoff on hardware, keep preset value
      min: 0,
      max: 127,
      label: "Amp Release",
    },

    // === FILTER ENVELOPE ===
    {
      number: 16,
      enabled: false, // Disabled - filter env attack kept stable for consistent response
      min: 0,
      max: 127,
      label: "Filter Env Attack",
    },
    {
      number: 17,
      enabled: true, // ✅ Filter Env Decay - safe to scramble
      min: 0,
      max: 127,
      label: "Filter Env Decay",
    },
    {
      number: 18,
      enabled: true, // ✅ Filter Env Sustain - safe to scramble
      min: 0,
      max: 127,
      label: "Filter Env Sustain",
    },
    {
      number: 19,
      enabled: true, // ✅ Filter Env Release - safe to scramble (less critical than amp release)
      min: 0,
      max: 127,
      label: "Filter Env Release",
    },
    {
      number: 20,
      enabled: true, // ✅ Filter Env Amount
      min: 0,
      max: 127,
      label: "Filter Env Amount",
    },
    {
      number: 21,
      enabled: true, // ✅ Filter Env Base Freq
      min: 0,
      max: 127,
      label: "Filter Env Base",
    },

    // === OSCILLATOR ===
    {
      number: 76,
      enabled: true, // ✅ Sound Controller 7 (often mapped to oscillator params)
      min: 0,
      max: 127,
      label: "Sound Controller 7",
    },
    {
      number: 77,
      enabled: false, // Sound Controller 8
      min: 0,
      max: 127,
      label: "Sound Controller 8",
    },
    {
      number: 78,
      enabled: false, // Sound Controller 9
      min: 0,
      max: 127,
      label: "Sound Controller 9",
    },
  ],
};

/**
 * Get the default hardware profile
 * Returns a fresh copy to avoid mutation
 */
export function getDefaultHardwareProfile(): DeviceProfile {
  return {
    ...DEFAULT_HARDWARE_PROFILE,
    ccConfig: DEFAULT_HARDWARE_PROFILE.ccConfig.map(cc => ({ ...cc })),
  };
}
